// Goals Tab Component
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Pencil, CheckCircle2, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { crmAPI } from '../../services/api';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';

const OVERALL_GOAL_OPTIONS = [
  { value: 'breeding', label: 'Breeding' },
  { value: 'seasons_shares', label: 'Seasons/Shares' },
  { value: 'selling', label: 'Selling' },
  { value: 'partnerships', label: 'Partnerships' },
  { value: 'racing', label: 'Racing' },
];

const GoalsTab = ({ account, onUpdate, associations = [], entities = [], canEditAccount = true }) => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  // ── Server state ──────────────────────────────────────────────────────────
  const { data: topHorses = [] } = useQuery({
    queryKey: QK.goalsTopHorses(account?.id),
    queryFn: async () => {
      const [ownershipRes, horsesRes] = await Promise.all([
        api.get(`/ownership-records?entity_id=${account.id}`),
        api.get('/horses'),
      ]);
      const horseIds = new Set((ownershipRes.data || []).map(o => o.horse_id));
      return (horsesRes.data || []).filter(h => h.is_top_horse && horseIds.has(h.id));
    },
    enabled: !!account?.id,
  });

  // ── UI state ──────────────────────────────────────────────────────────────
  const [isEditingOverall, setIsEditingOverall] = useState(false);
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [isEditingBreeding, setIsEditingBreeding] = useState(false);
  const [isEditingSeasons, setIsEditingSeasons] = useState(false);
  const [isEditingSelling, setIsEditingSelling] = useState(false);
  const [isEditingPartnerships, setIsEditingPartnerships] = useState(false);
  const [isEditingRacing, setIsEditingRacing] = useState(false);
  
  // Form data for each goal section
  const [breedingForm, setBreedingForm] = useState({});
  const [seasonsForm, setSeasonsForm] = useState({});
  const [sellingForm, setSellingForm] = useState({});
  const [partnershipsForm, setPartnershipsForm] = useState({});
  const [racingForm, setRacingForm] = useState({});
  
  // Sync form defaults from account prop whenever account changes
  useEffect(() => {
    if (account) {
      setSelectedGoals(account.overall_goals || []);
      setBreedingForm({
        breeding_goal_race: account.breeding_goal_race || false,
        breeding_goal_sell: account.breeding_goal_sell || false,
        number_of_mares_current: account.number_of_mares_current || '',
        number_of_mares_goal: account.number_of_mares_goal || '',
        goal_mare_portfolio_age_low: account.goal_mare_portfolio_age_low || '',
        goal_mare_portfolio_age_high: account.goal_mare_portfolio_age_high || '',
        goal_mare_portfolio_stakes_winner: account.goal_mare_portfolio_stakes_winner || false,
        goal_mare_portfolio_stakes_producer: account.goal_mare_portfolio_stakes_producer || false,
        goal_mare_portfolio_foals_in_front_low: account.goal_mare_portfolio_foals_in_front_low || '',
        goal_mare_portfolio_foals_in_front_high: account.goal_mare_portfolio_foals_in_front_high || '',
      });
      setSeasonsForm({
        seasons_booked_by: account.seasons_booked_by || '',
        season_fee_currency: account.season_fee_currency || 'USD',
        season_fee_range_low: account.season_fee_range_low || '',
        season_fee_range_high: account.season_fee_range_high || '',
        buy_season_no_guarantee: account.buy_season_no_guarantee || false,
        interested_in_shares: account.interested_in_shares || false,
        interested_in_breeding_rights: account.interested_in_breeding_rights || false,
      });
      setSellingForm({
        to_sell_weanling: account.to_sell_weanling || false,
        to_sell_yearling: account.to_sell_yearling || false,
        to_sell_2yo: account.to_sell_2yo || false,
        to_sell_broodmare: account.to_sell_broodmare || false,
        to_sell_racing_prospect: account.to_sell_racing_prospect || false,
        to_sell_hora: account.to_sell_hora || false,
        willing_to_sell_privately: account.willing_to_sell_privately || false,
        willing_to_sell_partial_interest: account.willing_to_sell_partial_interest || false,
        sells_at: account.sells_at || [],
        sells_through: account.sells_through || [],
        sells_at_other: account.sells_at_other || '',
        sells_through_other: account.sells_through_other || '',
      });
      setPartnershipsForm({
        interested_in_pinhook_partnerships: account.interested_in_pinhook_partnerships || false,
        interested_in_racehorse_partnerships: account.interested_in_racehorse_partnerships || false,
        interested_in_mare_partnerships: account.interested_in_mare_partnerships || false,
      });
      setRacingForm({
        racing_who_breaks_horses: account.racing_who_breaks_horses || '',
        racing_preferred_circuit: account.racing_preferred_circuit || '',
        racing_willing_to_buy_interest: account.racing_willing_to_buy_interest || false,
        racing_other_top_horses: account.racing_other_top_horses || '',
        goals_other_notes: account.goals_other_notes || '',
      });
    }
  }, [account?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveOverallGoals = async () => {
    setSaving(true);
    try {
      await crmAPI.update(account.id, { overall_goals: selectedGoals });
      toast.success('Overall goals updated');
      setIsEditingOverall(false);
      onUpdate?.();
    } catch (error) {
      console.error('Failed to update overall goals:', error);
      toast.error('Failed to update goals');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBreeding = async () => {
    setSaving(true);
    try {
      await crmAPI.update(account.id, breedingForm);
      toast.success('Breeding goals updated');
      setIsEditingBreeding(false);
      onUpdate?.();
    } catch (error) {
      console.error('Failed to update breeding goals:', error);
      toast.error('Failed to update goals');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSeasons = async () => {
    setSaving(true);
    try {
      await crmAPI.update(account.id, seasonsForm);
      toast.success('Seasons/Shares goals updated');
      setIsEditingSeasons(false);
      onUpdate?.();
    } catch (error) {
      console.error('Failed to update seasons goals:', error);
      toast.error('Failed to update goals');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSelling = async () => {
    setSaving(true);
    try {
      await crmAPI.update(account.id, sellingForm);
      toast.success('Selling goals updated');
      setIsEditingSelling(false);
      onUpdate?.();
    } catch (error) {
      console.error('Failed to update selling goals:', error);
      toast.error('Failed to update goals');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePartnerships = async () => {
    setSaving(true);
    try {
      await crmAPI.update(account.id, partnershipsForm);
      toast.success('Partnership goals updated');
      setIsEditingPartnerships(false);
      onUpdate?.();
    } catch (error) {
      console.error('Failed to update partnership goals:', error);
      toast.error('Failed to update goals');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRacing = async () => {
    setSaving(true);
    try {
      await crmAPI.update(account.id, racingForm);
      toast.success('Racing goals updated');
      setIsEditingRacing(false);
      onUpdate?.();
    } catch (error) {
      console.error('Failed to update racing goals:', error);
      toast.error('Failed to update goals');
    } finally {
      setSaving(false);
    }
  };

  const toggleGoal = (goalValue) => {
    if (selectedGoals.includes(goalValue)) {
      setSelectedGoals(selectedGoals.filter(g => g !== goalValue));
    } else {
      setSelectedGoals([...selectedGoals, goalValue]);
    }
  };

  // Helper to get entity name from associations
  const getEntityName = (entityId) => {
    const entity = entities.find(e => e.id === entityId);
    return entity ? entity.system_name : 'Unknown';
  };

  // Get associated trainers
  const trainers = associations.filter(a => 
    a.relationship === 'trainer' && a.status.startsWith('current')
  );

  // Get partnership associations
  const currentPartnerships = associations.filter(a => 
    a.relationship === 'partnership' && a.status.startsWith('current')
  );
  
  const pastPartnerships = associations.filter(a => 
    a.relationship === 'partnership' && a.status.startsWith('past')
  );

  const handleNavigateToEntity = (assoc) => {
    const otherId = assoc.crm_entity_1_id === account.id ? assoc.crm_entity_2_id : assoc.crm_entity_1_id;
    navigate(`/crm/accounts/${otherId}`);
  };

  return (
    <div className="space-y-6">
      {/* Top Row: Overall Goals and Other Goal Notes */}
      <div className="grid grid-cols-2 gap-6">
        {/* Overall Goals */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base">Overall Goals</CardTitle>
            {canEditAccount && !isEditingOverall && (
              <Button variant="ghost" size="icon" onClick={() => setIsEditingOverall(true)}>
                <Pencil className="w-4 h-4" />
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {OVERALL_GOAL_OPTIONS.map(option => {
                const isSelected = selectedGoals.includes(option.value);
                return (
                  <Button
                    key={option.value}
                    variant={isSelected ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => isEditingOverall && toggleGoal(option.value)}
                    disabled={!isEditingOverall}
                    className={`${!isEditingOverall ? 'cursor-default' : ''} disabled:opacity-100`}
                  >
                    {isSelected && <CheckCircle2 className="w-3 h-3 mr-1" />}
                    {option.label}
                  </Button>
                );
              })}
            </div>
            {isEditingOverall && (
              <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                <Button variant="outline" size="sm" onClick={() => { setSelectedGoals(account.overall_goals || []); setIsEditingOverall(false); }} disabled={saving}>Cancel</Button>
                <Button size="sm" onClick={handleSaveOverallGoals} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Other Goal Notes */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base">Other Goal Notes</CardTitle>
            {canEditAccount && !isEditingRacing && (
              <Button variant="ghost" size="icon" onClick={() => setIsEditingRacing(true)}>
                <Pencil className="w-4 h-4" />
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {isEditingRacing ? (
              <Textarea
                value={racingForm.goals_other_notes}
                onChange={(e) => setRacingForm({...racingForm, goals_other_notes: e.target.value})}
                placeholder="Additional goal notes..."
                rows={4}
                className="resize-none"
              />
            ) : (
              <div className="text-sm min-h-[100px]">
                {account.goals_other_notes ? (
                  <p className="whitespace-pre-wrap">{account.goals_other_notes}</p>
                ) : (
                  <p className="text-muted-foreground">No additional notes.</p>
                )}
              </div>
            )}
            {isEditingRacing && (
              <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                <Button variant="outline" size="sm" onClick={() => { setRacingForm({...racingForm, goals_other_notes: account.goals_other_notes || ''}); setIsEditingRacing(false); }} disabled={saving}>Cancel</Button>
                <Button size="sm" onClick={handleSaveRacing} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Goal-Specific Sections in Two Columns */}
      <div className="grid grid-cols-2 gap-6">

      {/* Breeding Goals */}
      {selectedGoals.includes('breeding') && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base">Breeding Goals</CardTitle>
            {canEditAccount && !isEditingBreeding && (
              <Button variant="ghost" size="icon" onClick={() => setIsEditingBreeding(true)}>
                <Pencil className="w-4 h-4" />
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Goals:</Label>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isEditingBreeding ? breedingForm.breeding_goal_race : account.breeding_goal_race}
                      onChange={(e) => setBreedingForm({...breedingForm, breeding_goal_race: e.target.checked})}
                      className="rounded"
                      disabled={!isEditingBreeding}
                    />
                    <span className="text-sm">Race</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isEditingBreeding ? breedingForm.breeding_goal_sell : account.breeding_goal_sell}
                      onChange={(e) => setBreedingForm({...breedingForm, breeding_goal_sell: e.target.checked})}
                      className="rounded"
                      disabled={!isEditingBreeding}
                    />
                    <span className="text-sm">Sell</span>
                  </label>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Number of Mares</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm w-20">Current:</Label>
                    <Input
                      type="number"
                      value={isEditingBreeding ? breedingForm.number_of_mares_current : (account.number_of_mares_current || '')}
                      onChange={(e) => setBreedingForm({...breedingForm, number_of_mares_current: e.target.value})}
                      placeholder="0"
                      className="flex-1 disabled:opacity-100"
                      disabled={!isEditingBreeding}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm w-20">Goals:</Label>
                    <Input
                      type="number"
                      value={isEditingBreeding ? breedingForm.number_of_mares_goal : (account.number_of_mares_goal || '')}
                      onChange={(e) => setBreedingForm({...breedingForm, number_of_mares_goal: e.target.value})}
                      placeholder="0"
                      className="flex-1 disabled:opacity-100"
                      disabled={!isEditingBreeding}
                    />
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <Label className="text-sm font-medium">Goals for Mare Portfolio</Label>
                
                <div className="flex items-center gap-2">
                  <Label className="text-sm w-32">Ages:</Label>
                  <Input
                    type="number"
                    value={isEditingBreeding ? breedingForm.goal_mare_portfolio_age_low : (account.goal_mare_portfolio_age_low || '')}
                    onChange={(e) => setBreedingForm({...breedingForm, goal_mare_portfolio_age_low: e.target.value})}
                    placeholder="Min"
                    className="w-24 disabled:opacity-100"
                    disabled={!isEditingBreeding}
                  />
                  <span className="text-sm">to</span>
                  <Input
                    type="number"
                    value={isEditingBreeding ? breedingForm.goal_mare_portfolio_age_high : (account.goal_mare_portfolio_age_high || '')}
                    onChange={(e) => setBreedingForm({...breedingForm, goal_mare_portfolio_age_high: e.target.value})}
                    placeholder="Max"
                    className="w-24 disabled:opacity-100"
                    disabled={!isEditingBreeding}
                  />
                </div>
                
                <div className="flex items-center gap-4">
                  <Label className="text-sm">Stakes Winner</Label>
                  <Switch
                    checked={isEditingBreeding ? breedingForm.goal_mare_portfolio_stakes_winner : account.goal_mare_portfolio_stakes_winner}
                    onCheckedChange={(checked) => setBreedingForm({...breedingForm, goal_mare_portfolio_stakes_winner: checked})}
                    disabled={!isEditingBreeding}
                    className="disabled:opacity-100"
                  />
                </div>
                
                <div className="flex items-center gap-4">
                  <Label className="text-sm">Stakes Producer</Label>
                  <Switch
                    checked={isEditingBreeding ? breedingForm.goal_mare_portfolio_stakes_producer : account.goal_mare_portfolio_stakes_producer}
                    onCheckedChange={(checked) => setBreedingForm({...breedingForm, goal_mare_portfolio_stakes_producer: checked})}
                    disabled={!isEditingBreeding}
                    className="disabled:opacity-100"
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <Label className="text-sm w-32">Foals in Front:</Label>
                  <Input
                    type="number"
                    value={isEditingBreeding ? breedingForm.goal_mare_portfolio_foals_in_front_low : (account.goal_mare_portfolio_foals_in_front_low || '')}
                    onChange={(e) => setBreedingForm({...breedingForm, goal_mare_portfolio_foals_in_front_low: e.target.value})}
                    placeholder="Min"
                    className="w-24 disabled:opacity-100"
                    disabled={!isEditingBreeding}
                  />
                  <span className="text-sm">-</span>
                  <Input
                    type="number"
                    value={isEditingBreeding ? breedingForm.goal_mare_portfolio_foals_in_front_high : (account.goal_mare_portfolio_foals_in_front_high || '')}
                    onChange={(e) => setBreedingForm({...breedingForm, goal_mare_portfolio_foals_in_front_high: e.target.value})}
                    placeholder="Max"
                    className="w-24 disabled:opacity-100"
                    disabled={!isEditingBreeding}
                  />
                </div>
              </div>
            </div>
            {isEditingBreeding && (
              <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                <Button variant="outline" size="sm" onClick={() => { setBreedingForm({ breeding_goal_race: account.breeding_goal_race || false, breeding_goal_sell: account.breeding_goal_sell || false, number_of_mares_current: account.number_of_mares_current || '', number_of_mares_goal: account.number_of_mares_goal || '', goal_mare_portfolio_age_low: account.goal_mare_portfolio_age_low || '', goal_mare_portfolio_age_high: account.goal_mare_portfolio_age_high || '', goal_mare_portfolio_stakes_winner: account.goal_mare_portfolio_stakes_winner || false, goal_mare_portfolio_stakes_producer: account.goal_mare_portfolio_stakes_producer || false, goal_mare_portfolio_foals_in_front_low: account.goal_mare_portfolio_foals_in_front_low || '', goal_mare_portfolio_foals_in_front_high: account.goal_mare_portfolio_foals_in_front_high || '' }); setIsEditingBreeding(false); }} disabled={saving}>Cancel</Button>
                <Button size="sm" onClick={handleSaveBreeding} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Seasons/Shares Goals */}
      {selectedGoals.includes('seasons_shares') && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base">Seasons/Shares Goals</CardTitle>
            {canEditAccount && !isEditingSeasons && (
              <Button variant="ghost" size="icon" onClick={() => setIsEditingSeasons(true)}>
                <Pencil className="w-4 h-4" />
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Label className="text-sm w-40">Seasons Booked By</Label>
                <Input
                  value={isEditingSeasons ? seasonsForm.seasons_booked_by : (account.seasons_booked_by || '')}
                  onChange={(e) => setSeasonsForm({...seasonsForm, seasons_booked_by: e.target.value})}
                  placeholder="Enter date or details"
                  className="flex-1 disabled:opacity-100"
                  disabled={!isEditingSeasons}
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Label className="text-sm w-40">Season Fee Range:</Label>
                <Select 
                  value={isEditingSeasons ? seasonsForm.season_fee_currency : (account.season_fee_currency || 'USD')}
                  onValueChange={(val) => setSeasonsForm({...seasonsForm, season_fee_currency: val})}
                  disabled={!isEditingSeasons}
                >
                  <SelectTrigger className="w-24 disabled:opacity-100">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="AUD">AUD</SelectItem>
                    <SelectItem value="CAD">CAD</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  value={isEditingSeasons ? seasonsForm.season_fee_range_low : (account.season_fee_range_low || '')}
                  onChange={(e) => setSeasonsForm({...seasonsForm, season_fee_range_low: e.target.value})}
                  placeholder="Min"
                  className="w-32 disabled:opacity-100"
                  disabled={!isEditingSeasons}
                />
                <span className="text-sm">to</span>
                <Input
                  type="number"
                  value={isEditingSeasons ? seasonsForm.season_fee_range_high : (account.season_fee_range_high || '')}
                  onChange={(e) => setSeasonsForm({...seasonsForm, season_fee_range_high : e.target.value})}
                  placeholder="Max"
                  className="w-32 disabled:opacity-100"
                  disabled={!isEditingSeasons}
                />
              </div>
              
              <div className="flex items-center gap-4">
                <Label className="text-sm">Buy Season with No Guarantee</Label>
                <Switch
                  checked={isEditingSeasons ? seasonsForm.buy_season_no_guarantee : account.buy_season_no_guarantee}
                  onCheckedChange={(checked) => setSeasonsForm({...seasonsForm, buy_season_no_guarantee: checked})}
                  disabled={!isEditingSeasons}
                  className="disabled:opacity-100"
                />
              </div>
              
              <div className="flex items-center gap-4">
                <Label className="text-sm">Interested in Shares</Label>
                <Switch
                  checked={isEditingSeasons ? seasonsForm.interested_in_shares : account.interested_in_shares}
                  onCheckedChange={(checked) => setSeasonsForm({...seasonsForm, interested_in_shares: checked})}
                  disabled={!isEditingSeasons}
                  className="disabled:opacity-100"
                />
              </div>
              
              <div className="flex items-center gap-4">
                <Label className="text-sm">Interested in Breeding Rights</Label>
                <Switch
                  checked={isEditingSeasons ? seasonsForm.interested_in_breeding_rights : account.interested_in_breeding_rights}
                  onCheckedChange={(checked) => setSeasonsForm({...seasonsForm, interested_in_breeding_rights: checked})}
                  disabled={!isEditingSeasons}
                  className="disabled:opacity-100"
                />
              </div>
            </div>
            {isEditingSeasons && (
              <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                <Button variant="outline" size="sm" onClick={() => { setSeasonsForm({ seasons_booked_by: account.seasons_booked_by || '', season_fee_currency: account.season_fee_currency || 'USD', season_fee_range_low: account.season_fee_range_low || '', season_fee_range_high: account.season_fee_range_high || '', buy_season_no_guarantee: account.buy_season_no_guarantee || false, interested_in_shares: account.interested_in_shares || false, interested_in_breeding_rights: account.interested_in_breeding_rights || false }); setIsEditingSeasons(false); }} disabled={saving}>Cancel</Button>
                <Button size="sm" onClick={handleSaveSeasons} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Selling Goals */}
      {selectedGoals.includes('selling') && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base">Selling Goals</CardTitle>
            {canEditAccount && !isEditingSelling && (
              <Button variant="ghost" size="icon" onClick={() => setIsEditingSelling(true)}>
                <Pencil className="w-4 h-4" />
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium mb-2">To Sell:</Label>
                <div className="space-y-2">
                  {[
                    { field: 'to_sell_weanling', label: 'Weanling' },
                    { field: 'to_sell_yearling', label: 'Yearling' },
                    { field: 'to_sell_2yo', label: '2 Yr Old' },
                    { field: 'to_sell_broodmare', label: 'Broodmare' },
                    { field: 'to_sell_racing_prospect', label: 'Racing Prospect' },
                    { field: 'to_sell_hora', label: 'H.O.R.A.' },
                  ].map(({ field, label }) => (
                    <label key={field} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={isEditingSelling ? sellingForm[field] : account[field]}
                        onChange={(e) => setSellingForm({...sellingForm, [field]: e.target.checked})}
                        className="rounded disabled:opacity-100"
                        disabled={!isEditingSelling}
                      />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-2">Willing to Sell:</Label>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-4">
                    <Label className="text-sm">Privately</Label>
                    <Switch
                      checked={isEditingSelling ? sellingForm.willing_to_sell_privately : account.willing_to_sell_privately}
                      onCheckedChange={(checked) => setSellingForm({...sellingForm, willing_to_sell_privately: checked})}
                      disabled={!isEditingSelling}
                      className="disabled:opacity-100"
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <Label className="text-sm">Partial Interest</Label>
                    <Switch
                      checked={isEditingSelling ? sellingForm.willing_to_sell_partial_interest : account.willing_to_sell_partial_interest}
                      onCheckedChange={(checked) => setSellingForm({...sellingForm, willing_to_sell_partial_interest: checked})}
                      disabled={!isEditingSelling}
                      className="disabled:opacity-100"
                    />
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Sell At:</Label>
                <div className="space-y-2">
                  {['auctions', 'digital_sales'].map((option) => (
                    <label key={option} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={(isEditingSelling ? sellingForm.sells_at : (account.sells_at || [])).includes(option)}
                        onChange={(e) => {
                          const current = sellingForm.sells_at || [];
                          if (e.target.checked) {
                            setSellingForm({...sellingForm, sells_at: [...current, option]});
                          } else {
                            setSellingForm({...sellingForm, sells_at: current.filter(v => v !== option)});
                          }
                        }}
                        className="rounded disabled:opacity-100"
                        disabled={!isEditingSelling}
                      />
                      <span className="text-sm capitalize">{option.replace('_', ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Sells Through:</Label>
                <div className="space-y-2">
                  {[
                    { value: 'keeneland', label: 'Keeneland' },
                    { value: 'fasig_tipton', label: 'Fasig-Tipton' },
                    { value: 'obs', label: 'OBS' },
                    { value: 'inglis', label: 'Inglis' },
                    { value: 'other', label: 'Other' },
                  ].map(({ value, label }) => (
                    <label key={value} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={(isEditingSelling ? sellingForm.sells_through : (account.sells_through || [])).includes(value)}
                        onChange={(e) => {
                          const current = sellingForm.sells_through || [];
                          if (e.target.checked) {
                            setSellingForm({...sellingForm, sells_through: [...current, value]});
                          } else {
                            setSellingForm({...sellingForm, sells_through: current.filter(v => v !== value)});
                          }
                        }}
                        className="rounded disabled:opacity-100"
                        disabled={!isEditingSelling}
                      />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
                {((isEditingSelling && sellingForm.sells_through?.includes('other')) || 
                  (!isEditingSelling && account.sells_through?.includes('other'))) && (
                  <Input
                    value={isEditingSelling ? (sellingForm.sells_through_other || '') : (account.sells_through_other || '')}
                    onChange={(e) => setSellingForm({...sellingForm, sells_through_other: e.target.value})}
                    placeholder="Enter other sales company"
                    className="mt-2 disabled:opacity-100"
                    disabled={!isEditingSelling}
                  />
                )}
              </div>
            </div>
            {isEditingSelling && (
              <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                <Button variant="outline" size="sm" onClick={() => { setSellingForm({ to_sell_weanling: account.to_sell_weanling || false, to_sell_yearling: account.to_sell_yearling || false, to_sell_2yo: account.to_sell_2yo || false, to_sell_broodmare: account.to_sell_broodmare || false, to_sell_racing_prospect: account.to_sell_racing_prospect || false, to_sell_hora: account.to_sell_hora || false, willing_to_sell_privately: account.willing_to_sell_privately || false, willing_to_sell_partial_interest: account.willing_to_sell_partial_interest || false, sells_at: account.sells_at || [], sells_through: account.sells_through || [], sells_at_other: account.sells_at_other || '', sells_through_other: account.sells_through_other || '' }); setIsEditingSelling(false); }} disabled={saving}>Cancel</Button>
                <Button size="sm" onClick={handleSaveSelling} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Partnerships Goals */}
      {selectedGoals.includes('partnerships') && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base">Partnership Goals</CardTitle>
            {canEditAccount && !isEditingPartnerships && (
              <Button variant="ghost" size="icon" onClick={() => setIsEditingPartnerships(true)}>
                <Pencil className="w-4 h-4" />
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium mb-3 block">Partnerships of Interest</Label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isEditingPartnerships ? partnershipsForm.interested_in_pinhook_partnerships : account.interested_in_pinhook_partnerships}
                      onChange={(e) => setPartnershipsForm({...partnershipsForm, interested_in_pinhook_partnerships: e.target.checked})}
                      className="rounded disabled:opacity-100"
                      disabled={!isEditingPartnerships}
                    />
                    <span className="text-sm">Pinhooks</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isEditingPartnerships ? partnershipsForm.interested_in_racehorse_partnerships : account.interested_in_racehorse_partnerships}
                      onChange={(e) => setPartnershipsForm({...partnershipsForm, interested_in_racehorse_partnerships: e.target.checked})}
                      className="rounded disabled:opacity-100"
                      disabled={!isEditingPartnerships}
                    />
                    <span className="text-sm">Racehorses</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isEditingPartnerships ? partnershipsForm.interested_in_mare_partnerships : account.interested_in_mare_partnerships}
                      onChange={(e) => setPartnershipsForm({...partnershipsForm, interested_in_mare_partnerships: e.target.checked})}
                      className="rounded disabled:opacity-100"
                      disabled={!isEditingPartnerships}
                    />
                    <span className="text-sm">Mares</span>
                  </label>
                </div>
              </div>

              {/* Current Partnerships List - Always visible in view mode */}
              {canEditAccount && !isEditingPartnerships && (
                <>
                  <div className="pt-3 border-t">
                    <Label className="text-sm font-medium mb-2 block">Current Partnerships</Label>
                    {currentPartnerships.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No current partnerships</p>
                    ) : (
                      <div className="space-y-1">
                        {currentPartnerships.slice(0, 5).map(assoc => {
                          const otherId = assoc.crm_entity_1_id === account.id ? assoc.crm_entity_2_id : assoc.crm_entity_1_id;
                          return (
                            <div key={assoc.id} className="flex items-center gap-2">
                              <Link2 className="w-3 h-3 text-muted-foreground" />
                              <button
                                className="text-sm text-primary hover:underline"
                                onClick={() => handleNavigateToEntity(assoc)}
                              >
                                {getEntityName(otherId)}
                              </button>
                            </div>
                          );
                        })}
                        {currentPartnerships.length > 5 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            + {currentPartnerships.length - 5} more
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="pt-3 border-t">
                    <Label className="text-sm font-medium mb-2 block">Previous Partnerships</Label>
                    {pastPartnerships.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No previous partnerships</p>
                    ) : (
                      <div className="space-y-1">
                        {pastPartnerships.slice(0, 5).map(assoc => {
                          const otherId = assoc.crm_entity_1_id === account.id ? assoc.crm_entity_2_id : assoc.crm_entity_1_id;
                          return (
                            <div key={assoc.id} className="flex items-center gap-2">
                              <Link2 className="w-3 h-3 text-muted-foreground" />
                              <button
                                className="text-sm text-primary hover:underline"
                                onClick={() => handleNavigateToEntity(assoc)}
                              >
                                {getEntityName(otherId)}
                              </button>
                            </div>
                          );
                        })}
                        {pastPartnerships.length > 5 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            + {pastPartnerships.length - 5} more
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            {isEditingPartnerships && (
              <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                <Button variant="outline" size="sm" onClick={() => { setPartnershipsForm({ interested_in_pinhook_partnerships: account.interested_in_pinhook_partnerships || false, interested_in_racehorse_partnerships: account.interested_in_racehorse_partnerships || false, interested_in_mare_partnerships: account.interested_in_mare_partnerships || false }); setIsEditingPartnerships(false); }} disabled={saving}>Cancel</Button>
                <Button size="sm" onClick={handleSavePartnerships} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Racing Goals */}
      {selectedGoals.includes('racing') && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base">Racing Goals</CardTitle>
            {canEditAccount && !isEditingRacing && (
              <Button variant="ghost" size="icon" onClick={() => setIsEditingRacing(true)}>
                <Pencil className="w-4 h-4" />
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Label className="text-sm w-48">Who Breaks Horses?</Label>
                <Input
                  value={isEditingRacing ? racingForm.racing_who_breaks_horses : (account.racing_who_breaks_horses || '')}
                  onChange={(e) => setRacingForm({...racingForm, racing_who_breaks_horses: e.target.value})}
                  placeholder="Trainer or facility"
                  className="flex-1 disabled:opacity-100"
                  disabled={!isEditingRacing}
                />
              </div>

              {/* Trainers List - Always visible in view mode */}
              {canEditAccount && !isEditingRacing && (
                <div className="pt-1">
                  <Label className="text-sm font-medium mb-2 block">Trainer(s)</Label>
                  {trainers.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No trainers associated</p>
                  ) : (
                    <div className="space-y-1">
                      {trainers.slice(0, 5).map(assoc => {
                        const otherId = assoc.crm_entity_1_id === account.id ? assoc.crm_entity_2_id : assoc.crm_entity_1_id;
                        return (
                          <div key={assoc.id} className="flex items-center gap-2">
                            <Link2 className="w-3 h-3 text-muted-foreground" />
                            <button
                              className="text-sm text-primary hover:underline"
                              onClick={() => handleNavigateToEntity(assoc)}
                            >
                              {getEntityName(otherId)}
                            </button>
                          </div>
                        );
                      })}
                      {trainers.length > 5 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          + {trainers.length - 5} more
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex items-center gap-2">
                <Label className="text-sm w-48">Preferred Racing Circuit</Label>
                <Input
                  value={isEditingRacing ? racingForm.racing_preferred_circuit : (account.racing_preferred_circuit || '')}
                  onChange={(e) => setRacingForm({...racingForm, racing_preferred_circuit: e.target.value})}
                  placeholder="e.g., NYRA, Churchill Downs"
                  className="flex-1 disabled:opacity-100"
                  disabled={!isEditingRacing}
                />
              </div>
              
              <div className="flex items-center gap-4">
                <Label className="text-sm">Willing to Buy Interest in Horse on Track</Label>
                <Switch
                  checked={isEditingRacing ? racingForm.racing_willing_to_buy_interest : account.racing_willing_to_buy_interest}
                  onCheckedChange={(checked) => setRacingForm({...racingForm, racing_willing_to_buy_interest: checked})}
                  disabled={!isEditingRacing}
                  className="disabled:opacity-100"
                />
              </div>
              
              {/* Top Horses List */}
              {canEditAccount && !isEditingRacing && (
                <div className="pt-1">
                  <Label className="text-sm font-medium mb-2 block">Top Horses Campaigned</Label>
                  {topHorses.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No top horses owned</p>
                  ) : (
                    <div className="space-y-1">
                      {topHorses.slice(0, 5).map(horse => (
                        <div key={horse.id} className="flex items-center gap-2">
                          <Link2 className="w-3 h-3 text-muted-foreground" />
                          <button
                            className="text-sm text-primary hover:underline"
                            onClick={() => navigate(`/horses/${horse.id}`)}
                          >
                            {horse.system_name || horse.name || 'Unnamed Horse'}
                          </button>
                        </div>
                      ))}
                      {topHorses.length > 5 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          + {topHorses.length - 5} more
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              <div className="space-y-2">
                <Label className="text-sm">Other Top Horses Campaigned</Label>
                <Textarea
                  value={isEditingRacing ? racingForm.racing_other_top_horses : (account.racing_other_top_horses || '')}
                  onChange={(e) => setRacingForm({...racingForm, racing_other_top_horses: e.target.value})}
                  placeholder="List other top horses"
                  rows={3}
                  disabled={!isEditingRacing}
                  className="disabled:opacity-100"
                />
              </div>
            </div>
            {isEditingRacing && (
              <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                <Button variant="outline" size="sm" onClick={() => { setRacingForm({ racing_who_breaks_horses: account.racing_who_breaks_horses || '', racing_preferred_circuit: account.racing_preferred_circuit || '', racing_willing_to_buy_interest: account.racing_willing_to_buy_interest || false, racing_other_top_horses: account.racing_other_top_horses || '', goals_other_notes: account.goals_other_notes || '' }); setIsEditingRacing(false); }} disabled={saving}>Cancel</Button>
                <Button size="sm" onClick={handleSaveRacing} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      </div>
    </div>
  );
};

export default GoalsTab;
